import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { DocumentTextIcon } from '@heroicons/react/24/outline';

type PublicationDetailData = {
  _id: string;
  title: string;
  authors: string;
  venue?: string;
  abstract?: string;
  content?: string;
  year?: number;
  type: 'journal' | 'patent' | 'other';
  url?: string;
};

const typeLabelMap: Record<PublicationDetailData['type'], string> = {
  journal: 'Journal Article',
  patent: 'Patent',
  other: 'Other Output',
};

const renderHtml = (html?: string) => ({ __html: html || '' });
const stripHtml = (value: string = '') =>
  value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
const hasRichTextContent = (html?: string) => {
  const raw = html?.trim() || '';
  if (!raw) return false;
  return stripHtml(raw).length > 0 || /<img\s/i.test(raw);
};
const parseAuthorNames = (authorsHtml: string) =>
  stripHtml(authorsHtml)
    .split(/[，,]/)
    .map((name) => name.trim())
    .filter(Boolean);

const PublicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [pub, setPub] = useState<PublicationDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get<PublicationDetailData>(`/publications/${id}`);
        setPub(res.data);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('Publication not found.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  return (
    <section className="bg-surface min-h-full">
      <div className="mx-auto max-w-screen-2xl px-8 pb-20 pt-12">
        {loading ? (
          <p className="text-sm text-on-surface-variant">Loading...</p>
        ) : error ? (
          <div className="rounded-md bg-error-container px-4 py-3 text-sm text-on-error-container">
            {error}
          </div>
        ) : !pub ? (
          <p className="text-sm text-on-surface-variant">Publication not found.</p>
        ) : (
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <article className="space-y-10 lg:col-span-8">
              <header className="space-y-6">
                <nav className="text-sm text-on-surface-variant">
                  <Link to="/publications" className="hover:text-primary">Publications</Link>
                  <span className="mx-1.5">/</span>
                  <span>Details</span>
                </nav>

                <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wider text-on-surface-variant">
                  <span className="font-semibold text-primary">{typeLabelMap[pub.type]}</span>
                  {pub.year && (
                    <>
                      <span>•</span>
                      <span>Published: {pub.year}</span>
                    </>
                  )}
                </div>

                <h1
                  className="font-serif text-4xl font-bold leading-tight text-on-surface md:text-5xl"
                  dangerouslySetInnerHTML={renderHtml(pub.title)}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="um-card bg-surface-container-lowest px-4 py-3">
                    <p className="text-xs uppercase tracking-wider text-on-surface-variant">Source</p>
                    <p
                      className="mt-1 text-sm font-medium text-on-surface"
                      dangerouslySetInnerHTML={renderHtml(pub.venue || 'TBD')}
                    />
                  </div>
                  <div className="um-card bg-surface-container-lowest px-4 py-3">
                    <p className="text-xs uppercase tracking-wider text-on-surface-variant">Year</p>
                    <p className="mt-1 text-sm font-medium text-on-surface">{pub.year || 'TBD'}</p>
                  </div>
                  <div className="um-card bg-surface-container-lowest px-4 py-3">
                    <p className="text-xs uppercase tracking-wider text-on-surface-variant">Type</p>
                    <p className="mt-1 text-sm font-medium text-on-surface">{typeLabelMap[pub.type]}</p>
                  </div>
                </div>
              </header>

              <section className="um-card bg-surface-container-low p-8">
                <h2 className="text-sm uppercase tracking-widest text-on-surface-variant">Authors</h2>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {parseAuthorNames(pub.authors).map((name, idx) => (
                    <div key={`${name}-${idx}`} className="flex items-center gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-container text-sm font-semibold text-primary">
                        {name.charAt(0)}
                      </div>
                      <p className="font-semibold text-on-surface">{name}</p>
                    </div>
                  ))}
                </div>
              </section>

              {hasRichTextContent(pub.abstract) && (
                <section className="space-y-4">
                  <h2 className="font-serif text-3xl font-bold text-on-surface">Abstract</h2>
                  <div
                    className="rich-text text-lg leading-relaxed text-on-surface-variant"
                    dangerouslySetInnerHTML={renderHtml(pub.abstract)}
                  />
                </section>
              )}

              {hasRichTextContent(pub.content) && (
                <section className="space-y-4">
                  <h2 className="font-serif text-3xl font-bold text-on-surface">
                    Detailed Introduction
                  </h2>
                  <div
                    className="rich-text text-lg leading-relaxed text-on-surface-variant"
                    dangerouslySetInnerHTML={renderHtml(pub.content)}
                  />
                </section>
              )}
            </article>

            <aside className="space-y-8 lg:col-span-4">
              <div className="um-card sticky top-28 bg-surface-container-lowest/90 p-6 backdrop-blur-md">
                <div className="space-y-4">
                  {pub.url ? (
                    <a
                      href={pub.url}
                      target="_blank"
                      rel="noreferrer"
                      className="um-btn-primary w-full px-4 py-3 text-sm"
                    >
                      <DocumentTextIcon className="h-[18px] w-[18px]" />
                      <span>Open Source</span>
                    </a>
                  ) : (
                    <button className="um-btn-secondary w-full px-4 py-3 text-sm" disabled>
                      No PDF link
                    </button>
                  )}
                </div>
              </div>

            </aside>
          </div>
        )}
      </div>
    </section>
  );
};

export default PublicationDetail;

